'use client';

import { ReactNode, useEffect, useMemo, useState } from 'react';

import { JourneyType } from '@bytel/leviator';
import {
    Box,
    Container,
    DirectionEnum,
    FlexBox,
    FlexItem,
    GapSize,
    Section,
    Title,
    TitleLevels,
    TitleMarkup,
    TrilogyColor,
} from '@bytel/trilogy-react';

import {
    createVirtualCart,
    getVirtualCart,
    hasCartBeenUpdated,
    updateFaiQuotes,
    updateMobileQuotes,
} from '@services/simulator';
import { setSessionVariable } from '@services/storage';

import { getImpactSimulationServer } from '@actions/get-impacts';
import { getServerSession } from '@actions/get-session';
import { getSimulatedCartAction } from '@actions/simulate-impact';

import { CategoryCodeInfo, FaiType, MobilePlanType } from '@app-types/plan';
import { ImpactsSimulation, SimulatorPack } from '@app-types/simulator/simulator';
import { SimulatedCart, SimulatorCart } from '@app-types/simulator/virtual-cart';
import { ForbiddenUserType, UserType, UserTypeEnum } from '@app-types/user';

import { Ticket } from '@components/ticket/ticket';

import { useOnScreen } from '@hooks/use-on-screen';

import { scrollToHash } from '@helpers/document';
import { updateOffersWithEligibility } from '@helpers/eligibility';
import { paths } from '@helpers/path';
import { getProductNameByGencode } from '@helpers/product';
import { noop } from '@helpers/try-catch';

import { getClientProductsAction, getProducts } from '@promo-simulator/actions';
import { MobilePlans, Pack, StickyMenu } from '@promo-simulator/components';
import { sendPromoSimulatorPageTracking, sendResetFilterTracking } from '@promo-simulator/helpers';
import { ClickEvent } from '@trilogy-ds/react/lib/events/OnClickEvent';

import { ConnectionIncentive } from './connection-incentive';
import { FaiCard } from './fai-card';
import { ModalTlvRedirect } from './modals/tlv-redirect';

interface SimulatorProps {
    defaultPlans: { fai: FaiType[]; mobile: MobilePlanType[] };
    defaultSimulatedCart: SimulatedCart;
    categoryCodeInfo: CategoryCodeInfo[];
    children?: ReactNode;
}

function completeCart(cart: SimulatedCart, defaultPlans: SimulatorProps['defaultPlans']): SimulatedCart {
    cart.quotes.forEach((quote) => {
        quote.products.forEach((product) => {
            product.name =
                getProductNameByGencode(product.gencode, [...defaultPlans.fai, ...defaultPlans.mobile]) || product.name;
        });
    });
    return cart;
}

export function Simulator({
    defaultPlans,
    defaultSimulatedCart,
    categoryCodeInfo,
    children,
}: Readonly<SimulatorProps>) {
    defaultSimulatedCart = completeCart(defaultSimulatedCart, defaultPlans);
    const [numberOfPlan, setNumberOfPlan] = useState(1);
    const [faiPlans, setFaiPlans] = useState<FaiType[]>(defaultPlans.fai);
    const [mobilePlans, setMobilePlans] = useState<MobilePlanType[]>(defaultPlans.mobile);
    const [impactSimulation, setImpactSimulation] = useState<ImpactsSimulation | undefined>();
    const [user, setUser] = useState<UserType>();
    const [simulatedCart, setSimulatedCart] = useState<SimulatedCart>(defaultSimulatedCart);
    const [isLoading, setIsLoading] = useState(false);
    const [shouldShowRedirectModal, setShouldShowRedirectModal] = useState(false);
    const { ref: partialRef, isVisible: isPartialVisible } = useOnScreen(true);
    const { ref: ctaRef, isVisible: isCtaVisible } = useOnScreen(false);
    const { ref: configuratorRef, isVisible: isConfiguratorVisible } = useOnScreen(true);

    const [selectedFaiPlan, setSelectedFaiPlan] = useState(
        defaultPlans.fai.find((p) => p.isDefault) ?? defaultPlans.fai[0]!,
    );
    const initialVirtualCart = createVirtualCart();
    const [virtualCart, setVirtualCart] = useState<SimulatorCart>(initialVirtualCart);

    const setFaiPlansFilteredByCategory = (plans: FaiType[]) => {
        const plansInCategory: FaiType[] = [];

        categoryCodeInfo.forEach((c) => {
            const plan = plans.find(
                (p) =>
                    p.rangeNg === c.rangeNg &&
                    p.commercialSeries === c.commercialSeries &&
                    p.technology === c.technology,
            );

            if (plan) {
                plansInCategory.push(plan);
            }
        });

        setFaiPlans(plansInCategory);
    };

    const onSelectedPack = async (pack: SimulatorPack) => {
        setNumberOfPlan(pack.nbPlan);
    };

    const onSelectedBox = async (plan: FaiType) => {
        if (virtualCart) {
            setSelectedFaiPlan(plan);
            virtualCart?.cart.quotes
                .filter((q) => q.type === JourneyType.MOBILE_ACQUISITION)
                .forEach((q) => (q.isCurrent = false));
            updateFaiQuotes(virtualCart, plan);
            await refreshData(virtualCart);
        }
    };

    const onSelectedPlans = async (plans: MobilePlanType[]) => {
        if (virtualCart) {
            const gencodes = plans.map((p, index) => {
                return {
                    gencode: p.gencode,
                    hasIncitationPortability: p.details.discounts.some(
                        (p) => p.isIncitation && p.marketingTypes.includes('PORTABILITE'),
                    ),
                    index,
                };
            });
            const newCart = updateMobileQuotes(virtualCart, gencodes);
            setVirtualCart(newCart);

            await refreshData(newCart);
        }
    };

    const refreshData = async (cart: SimulatorCart | undefined, forceRefresh: boolean = false) => {
        setIsLoading(true);
        try {
            await calculateImpacts();
            if (cart && (hasCartBeenUpdated(cart, getVirtualCart()) || forceRefresh)) {
                setSessionVariable('virtualCart', JSON.stringify(cart));
                const results = await Promise.all([getProducts(cart), getSimulatedCartAction(cart)]);
                const faiResult = updateOffersWithEligibility(results[0].fai);
                setFaiPlansFilteredByCategory(faiResult);
                setMobilePlans(results[0].mobile);
                const simulatedCart = results[1];
                if (simulatedCart) {
                    const completedCart = completeCart(simulatedCart, defaultPlans);
                    setSimulatedCart(completedCart);

                    // Set selectedFaiPlan based on the cart contents, or the default plan.
                    const acquisitionFixeQuote = completedCart.quotes.find(
                        (quote) => quote.type === JourneyType.FAI_ACQUISITION,
                    );
                    if (acquisitionFixeQuote) {
                        const planInCart = defaultPlans.fai.find(
                            (plan) => plan.gencode === acquisitionFixeQuote.products[0]?.gencode,
                        );
                        setSelectedFaiPlan(planInCart ?? defaultPlans.fai[0]!);
                    } else {
                        setSelectedFaiPlan(defaultPlans.fai[0]!);
                    }
                }
            }
        } catch (error) {
            console.error('Impossible de rafraichir le panier virtuel', error);
        }

        setIsLoading(false);
    };

    const calculateImpacts = async () => {
        if (user && virtualCart) {
            const impacts = await getImpactSimulationServer(
                virtualCart.cart.quotes.flatMap((quote) => quote.products.map((p) => p.gencode)),
            );
            if (impacts) {
                setImpactSimulation(impacts);
            }
        }
    };

    const onTicketValidate: ClickEvent = async (event) => {
        sendResetFilterTracking(
            (event as Event & { target: HTMLInputElement }).target.innerText,
            simulatedCart,
            selectedFaiPlan,
        );

        if (virtualCart.cart.quotes.length > 2) {
            setShouldShowRedirectModal(true);
        }

        if (virtualCart.cart.quotes.length === 2) {
            const mobileGencode = virtualCart.cart.quotes.find((q) => q.type === JourneyType.MOBILE_ACQUISITION)
                ?.products[0]?.gencode;
            if (mobileGencode) {
                const gamme = `${selectedFaiPlan?.rangeNg}_${selectedFaiPlan?.commercialSeries}_${selectedFaiPlan?.technology}`;
                location.href = paths.directCart(mobileGencode, gamme?.toLowerCase(), 'fromPack');
            }
        }

        setSessionVariable('virtualCart', JSON.stringify(initialVirtualCart));
    };

    useEffect(() => {
        sendPromoSimulatorPageTracking();
        updateOffersWithEligibility(defaultPlans.fai);

        const fetchClientData = async () => {
            const session = await getServerSession();
            if (session) {
                setUser(session.user as UserType | undefined);
                const planData = await getClientProductsAction();
                if (planData) {
                    setFaiPlansFilteredByCategory(planData.fai);
                    setMobilePlans(planData.mobile);
                }
            }
        };

        fetchClientData().catch(noop);

        const newCart = getVirtualCart();
        setSessionVariable('virtualCart', JSON.stringify(newCart));
        setVirtualCart(newCart);

        scrollToHash();
    }, []);

    useEffect(() => {
        if (user && virtualCart) {
            void refreshData(virtualCart, true);
        }
    }, [user?.sub, virtualCart]);

    return (
        <>
            <StickyTicket
                onClick={onTicketValidate}
                simulatedCart={simulatedCart}
                isVisibleDesktop={!isConfiguratorVisible}
                isVisibleMobile={!isCtaVisible && !isPartialVisible}
            />
            <Section backgroundColor={TrilogyColor.NEUTRAL_FADE}>
                <Container>
                    <div ref={partialRef} />
                    {(!user || !ForbiddenUserType.includes(user.user_type as UserTypeEnum)) && (
                        <ConnectionIncentive user={user} simulation={impactSimulation} cart={simulatedCart} />
                    )}
                    <FlexBox direction={DirectionEnum.COLUMN} ref={configuratorRef} gap={GapSize.FIVE}>
                        <FlexItem id='avantages'>{children}</FlexItem>
                        <FlexItem id='configurateur'>
                            <FlexBox direction={DirectionEnum.COLUMN} gap={GapSize.EIGHT}>
                                <FlexItem>
                                    <Title level={TitleLevels.FOUR} markup={TitleMarkup.H3}>
                                        1. Sélectionnez votre pack
                                    </Title>
                                    <Pack onSelectedPackChange={onSelectedPack} />
                                </FlexItem>
                                <FlexItem>
                                    <FlexBox>
                                        <FlexItem size={{ tablet: 12, desktop: 8 }}>
                                            {faiPlans.length > 0 && (
                                                <>
                                                    <Title level={TitleLevels.FOUR} markup={TitleMarkup.H3}>
                                                        2. Choisissez votre Bbox
                                                    </Title>
                                                    <FaiCard
                                                        plans={faiPlans}
                                                        selectedPlan={selectedFaiPlan}
                                                        onSelectedPlan={onSelectedBox}
                                                    />
                                                </>
                                            )}
                                            {mobilePlans.length > 0 && (
                                                <>
                                                    <Title level={TitleLevels.FOUR} markup={TitleMarkup.H3}>
                                                        3. Choisissez votre forfait
                                                    </Title>
                                                    <MobilePlans
                                                        plans={mobilePlans}
                                                        planToShow={numberOfPlan}
                                                        onPlanChanges={onSelectedPlans}
                                                    />
                                                </>
                                            )}
                                        </FlexItem>
                                        <FlexItem size={{ tablet: 12, desktop: 4 }}>
                                            <div className='is-marginless is-sticky-top' style={{ top: -12 }}>
                                                <div className='spacer-ticket' />
                                                <Box shadowless backgroundColor={TrilogyColor.INFO}>
                                                    <Ticket
                                                        title='Mes achats et économies'
                                                        cart={simulatedCart}
                                                        virtualCart={virtualCart}
                                                        isLoading={isLoading}
                                                        simulatorPlans={[...faiPlans, ...mobilePlans]}
                                                        onValidate={onTicketValidate}
                                                        hideRef={ctaRef}
                                                        simulation={impactSimulation}
                                                    />
                                                </Box>
                                            </div>
                                        </FlexItem>
                                    </FlexBox>
                                </FlexItem>
                            </FlexBox>
                        </FlexItem>
                    </FlexBox>
                    {shouldShowRedirectModal ? (
                        <ModalTlvRedirect onClose={() => setShouldShowRedirectModal(false)} />
                    ) : (
                        ''
                    )}
                </Container>
            </Section>
        </>
    );
}

function StickyTicket({
    simulatedCart,
    isVisibleMobile,
    isVisibleDesktop,
    onClick,
}: Readonly<{
    simulatedCart: SimulatedCart;
    isVisibleMobile: boolean;
    isVisibleDesktop: boolean;
    onClick: ClickEvent;
}>) {
    const { fai, plans, cta } = useMemo(() => {
        if (!simulatedCart || (simulatedCart.quotes?.length ?? 0) < 2) {
            return { fai: undefined, plans: [], cta: '' };
        }
        const faiProduct = simulatedCart.quotes.find((q) => q.type === JourneyType.FAI_ACQUISITION)?.products[0];
        const mobileProduct = simulatedCart.quotes
            .filter((q) => q.type === JourneyType.MOBILE_ACQUISITION)
            .flatMap((q) => q.products);

        return {
            fai: faiProduct,
            plans: mobileProduct,
            cta: mobileProduct.length !== 1 ? 'Continuer' : 'Tester mon éligibilité',
        };
    }, [simulatedCart]);

    if (!fai || plans.length === 0) {
        return null;
    }

    return (
        <StickyMenu
            isVisible={isVisibleMobile}
            className={isVisibleDesktop ? '' : 'is-hidden-tablet'}
            fai={fai}
            plans={plans}
            price={simulatedCart.price.monthly.prices.final}
            ctaLabel={cta}
            onTicketValidate={onClick}
        />
    );
}
